import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpContext } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AUTH_DAVESA_API } from 'app/core/auth-davesa/auth-davesa-api.component';

export interface SignatureWorkflowConfig {
  oid: string;
  studyId: number;
  name: string;
  description?: string;
  steps: SignatureStep[];
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface Response<T> {
  data: T;
}

// Simplified response DTO for the new architecture (no configuration layer)
export interface SignatureWorkflowResponseDto {
  steps: SignatureStepResponseDto[];
}

// Response DTO for individual signature steps
export interface SignatureStepResponseDto {
  oid: string;
  stepOrder: number;
  stepName: string;
  description?: string;
  label: string;
  allowedRoles: SignatureStepRoleResponseDto[];
}

// Response DTO for signature step roles
export interface SignatureStepRoleResponseDto {
  delegationRoleId: number;
  delegationRoleName: string;
  delegationRoleShortForm: string;
  delegationRoleDescription?: string;
}

export interface SignatureStep {
  oid: string;
  stepOrder: number;
  stepName: string;
  description?: string;
  label: string;
  allowedRoles: SignatureStepRole[];
}

export interface SignatureStepRole {
  delegationRoleId: number;
  delegationRoleName: string;
  delegationRoleShortForm: string;
  delegationRoleDescription?: string;
}

export interface CreateSignatureWorkflowDto {
  steps: CreateSignatureStepDto[];
}

export interface CreateSignatureStepDto {
  stepOrder: number;
  stepName: string;
  description?: string;
  label: string;
  allowedRoles: string[]; // Role IDs
}

export interface RoleOption {
  value: string;
  label: string;
  category: string;
  description?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SignatureWorkflowService {
  private readonly baseUrl = `api/v2/studies`;
  private configSubject = new BehaviorSubject<SignatureWorkflowConfig | null>(null);
  public config$ = this.configSubject.asObservable();

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    }),
    context: new HttpContext().set(AUTH_DAVESA_API, true)
  };

  // Cache for delegation roles to avoid multiple API calls
  private cachedRoles: RoleOption[] | null = null;
  private rolesPromise: Promise<RoleOption[]> | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Get available delegation role options from API (with caching)
   */
  async getAvailableRoles(): Promise<RoleOption[]> {
    // Return cached roles if available
    if (this.cachedRoles) {
      return this.cachedRoles;
    }

    // Return existing promise if already fetching
    if (this.rolesPromise) {
      return this.rolesPromise;
    }

    // Fetch roles from API
    this.rolesPromise = this.fetchRolesFromApi();

    try {
      const roles = await this.rolesPromise;
      this.cachedRoles = roles;
      return roles;
    } finally {
      this.rolesPromise = null;
    }
  }

  /**
   * Fetch roles from API (internal method)
   */
  private async fetchRolesFromApi(): Promise<RoleOption[]> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ data: RoleOption[] }>(`${this.baseUrl}/delegation-roles`, this.httpOptions)
      );
      return response.data || [];
    } catch (error) {
      console.error('Error fetching delegation roles:', error);
      // Return empty array as fallback - user will need to authenticate first
      return [];
    }
  }

  /**
   * Clear cached roles (useful for refreshing data)
   */
  clearCachedRoles(): void {
    this.cachedRoles = null;
    this.rolesPromise = null;
  }

  /**
   * Delete a specific signature step from workflow configuration
   */
  deleteSignatureStep(studyOid: string, stepOid: string): Promise<SignatureWorkflowResponseDto> {
    return firstValueFrom(
      this.http.delete<SignatureWorkflowResponseDto>(
        `${this.baseUrl}/${studyOid}/signature-workflow/steps/${stepOid}`,
        this.httpOptions
      )
    );
  }

  /**
   * Get roles filtered by category
   */
  async getRolesByCategory(category: string): Promise<RoleOption[]> {
    const roles = await this.getAvailableRoles();
    return roles.filter(role => role.category === category);
  }

  /**
   * Get all roles grouped by category
   */
  async getAllRolesByCategory(): Promise<{ [category: string]: RoleOption[] }> {
    const roles = await this.getAvailableRoles();
    return roles.reduce((groups, role) => {
      if (!groups[role.category]) {
        groups[role.category] = [];
      }
      groups[role.category].push(role);
      return groups;
    }, {} as { [category: string]: RoleOption[] });
  }

  /**
   * Get role categories
   */
  async getCategories(): Promise<string[]> {
    const roles = await this.getAvailableRoles();
    const categories = [...new Set(roles.map(role => role.category))];
    return categories.sort(); // Sort categories alphabetically
  }

  /**
   * Get role label by value
   */
  async getRoleLabel(roleValue: string): Promise<string> {
    try {
      const roles = await this.getAvailableRoles();
      const role = roles.find(r => r.value === roleValue);
      return role?.label || `Role ${roleValue}`;
    } catch (error) {
      console.error('Error fetching role label:', error);
      return `Role ${roleValue}`;
    }
  }

  /**
   * Get role description by value
   */
  async getRoleDescription(roleValue: string): Promise<string> {
    try {
      const roles = await this.getAvailableRoles();
      const role = roles.find(r => r.value === roleValue);
      return role?.description || 'Delegation role for signature workflow';
    } catch (error) {
      console.error('Error fetching role description:', error);
      return 'Delegation role for signature workflow';
    }
  }

  /**
   * Get signature workflow configuration for a study
   */
  async getWorkflowConfiguration(studyOid: string): Promise<SignatureWorkflowResponseDto> {
    console.log(`Loading signature workflow for study: ${studyOid}`);

    try {
      const response = await firstValueFrom(
        this.http.get<Response<SignatureWorkflowResponseDto>>(`${this.baseUrl}/${studyOid}/signature-workflow`, this.httpOptions)
      );
      const workflow = response.data;
      return workflow;
    } catch (error) {
      console.error('Signature workflow service error:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Save signature workflow configuration
   */
  async saveWorkflowConfiguration(studyOid: string, workflowDto: CreateSignatureWorkflowDto): Promise<SignatureWorkflowConfig> {
    try {
      const response = await firstValueFrom(
        this.http.post<Response<SignatureWorkflowConfig>>(`${this.baseUrl}/${studyOid}/signature-workflow`, workflowDto, this.httpOptions)
      );
      const savedConfig = response.data;
      this.configSubject.next(savedConfig);
      return savedConfig;
    } catch (error) {
      console.error('Error saving signature workflow:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Update signature workflow configuration
   */
  async updateWorkflowConfiguration(studyOid: string, workflowDto: CreateSignatureWorkflowDto): Promise<SignatureWorkflowResponseDto> {
    try {
      const response = await firstValueFrom(
        this.http.put<Response<SignatureWorkflowResponseDto>>(`${this.baseUrl}/${studyOid}/signature-workflow`, workflowDto, this.httpOptions)
      );
      const updatedWorkflow = response.data;
      return updatedWorkflow;
    } catch (error) {
      console.error('Error updating signature workflow:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Create new signature workflow configuration
   */
  async createWorkflowConfiguration(studyOid: string, workflowDto: CreateSignatureWorkflowDto): Promise<SignatureWorkflowResponseDto> {
    try {
      const response = await firstValueFrom(
        this.http.post<Response<SignatureWorkflowResponseDto>>(`${this.baseUrl}/${studyOid}/signature-workflow`, workflowDto, this.httpOptions)
      );
      const newWorkflow = response.data;
      return newWorkflow;
    } catch (error) {
      console.error('Error creating signature workflow:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Create a single signature step
   */
  async createSignatureStep(
    studyOid: string,
    step: { stepOrder: number; stepName: string; description?: string; label?: string; allowedRoles: string[] }
  ): Promise<SignatureWorkflowResponseDto> {
    try {
      const payload = {
        stepOrder: Number(step.stepOrder),
        stepName: step.stepName,
        description: step.description || '',
        label: step.label || step.stepName,
        allowedRoles: (step.allowedRoles || []).map((roleId: string) => ({ delegationRoleId: parseInt(roleId, 10) }))
      };

      const response = await firstValueFrom(
        this.http.post<Response<SignatureWorkflowResponseDto>>(
          `${this.baseUrl}/${studyOid}/signature-workflow/steps`,
          payload,
          this.httpOptions
        )
      );

      return (response as any)?.data ?? (response as any);
    } catch (error) {
      console.error('Error creating signature step:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Update a single signature step
   */
  async updateSignatureStep(
    studyOid: string,
    stepOid: string,
    step: { stepOrder: number; stepName: string; description?: string; label?: string; allowedRoles: string[] }
  ): Promise<SignatureWorkflowResponseDto> {
    try {
      const payload = {
        stepOrder: Number(step.stepOrder),
        stepName: step.stepName,
        description: step.description || '',
        label: step.label || step.stepName,
        allowedRoles: (step.allowedRoles || []).map((roleId: string) => ({ delegationRoleId: parseInt(roleId, 10) }))
      };

      const response = await firstValueFrom(
        this.http.put<Response<SignatureWorkflowResponseDto>>(
          `${this.baseUrl}/${studyOid}/signature-workflow/steps/${stepOid}`,
          payload,
          this.httpOptions
        )
      );

      return (response as any)?.data ?? (response as any);
    } catch (error) {
      console.error('Error updating signature step:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Delete signature workflow configuration
   */
  async deleteWorkflowConfiguration(studyOid: string, configOid: string): Promise<void> {
    try {
      await firstValueFrom(
        this.http.delete(`${this.baseUrl}/${studyOid}/signature-workflow/${configOid}`, this.httpOptions)
      );
      this.configSubject.next(null);
    } catch (error) {
      console.error('Error deleting signature workflow:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Generate workflow preview from steps
   */
  generateWorkflowPreview(config: CreateSignatureWorkflowDto): string[] {
    if (!config.steps || config.steps.length === 0) {
      return ['No steps configured'];
    }

    return config.steps
      .sort((a, b) => a.stepOrder - b.stepOrder)
      .map(step => {
        const roleLabels = step.allowedRoles.map(roleId => {
          // This would need to be resolved asynchronously, but for preview we'll use placeholders
          return `Role ${roleId}`;
        }).join(', ');
        return `${step.stepOrder}. ${step.stepName}: ${roleLabels}`;
      });
  }

  /**
   * Validate workflow configuration
   */
  validateWorkflowConfiguration(config: any): string[] {
    const validation = this.validateWorkflow(config);
    return validation.errors;
  }

  /**
   * Generate step summary
   */
  generateStepSummary(step: SignatureStep): string {
    const roleNames = step.allowedRoles.map(role => role.delegationRoleName).join(', ');
    return `${step.stepOrder}. ${step.stepName}: ${roleNames}`;
  }

  /**
   * Validate workflow
   */
  private validateWorkflow(config: CreateSignatureWorkflowDto): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!config.steps?.length) {
      errors.push('At least one step is required');
    }

    // Check for duplicate step orders
    const stepOrders = config.steps.map(step => step.stepOrder);
    const uniqueOrders = new Set(stepOrders);
    if (stepOrders.length !== uniqueOrders.size) {
      errors.push('Step orders must be unique');
    }

    // Check if steps are in sequential order starting from 1
    const sortedOrders = [...stepOrders].sort((a, b) => a - b);
    for (let i = 0; i < sortedOrders.length; i++) {
      if (sortedOrders[i] !== i + 1) {
        errors.push('Step orders must be sequential starting from 1');
        break;
      }
    }

    // Check if there's at least one participant step
    const hasParticipantStep = config.steps.some(step =>
      step.allowedRoles.some(role => role === 'PARTICIPANT')
    );
    if (!hasParticipantStep) {
      errors.push('At least one step must allow participant role');
    }

    // Validate each step
    config.steps.forEach((step, index) => {
      if (!step.stepName?.trim()) {
        errors.push(`Step ${index + 1}: Name is required`);
      }
      if (!step.label?.trim()) {
        errors.push(`Step ${index + 1}: Label is required`);
      }
      if (!step.allowedRoles?.length) {
        errors.push(`Step ${index + 1}: At least one role must be assigned`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Build workflow steps with personnel data
   * Centralized method to avoid code duplication
   */
  buildWorkflowStepsWithPersonnel(
    workflow: SignatureWorkflowResponseDto,
    personnel: any[],
    visit: any,
    getFullNameFn: (person: any) => string,
    findErrorReasonFn?: (stepId: string, expiredPersonnel: any[]) => string,
    expiredPersonnel: any[] = []
  ): any[] {
    if (!workflow?.steps) {
      return [];
    }

    const sortedSteps = [...workflow.steps].sort((a, b) => a.stepOrder - b.stepOrder);

    return sortedSteps.map(step => {
      const stepRoleIds = step.allowedRoles?.map(role => role.delegationRoleId) || [];
      const allowedRoleIds = stepRoleIds.filter((id): id is number => !!id);
      const primaryRole = step.allowedRoles?.[0];
      
      const rolePersonnel: any[] = [];
      
      // Add personnel from fetched list
      (personnel || []).forEach((member: any) => {
        const roleId = member?.delegationRoleId;
        if (roleId && member?.userId && allowedRoleIds.includes(roleId)) {
          rolePersonnel.push({
            userId: member.userId,
            firstName: member.firstName || '',
            middleName: member.middleName || '',
            lastName: member.lastName || '',
            suffixName: member.suffixName?.name || '',
            fullName: getFullNameFn(member),
            delegationRoleId: member.delegationRoleId,
            oid: member.oid || ''
          });
        }
      });

      // Find current assignment for this step
      const currentAssignment = visit?.signature_assignments?.find((a: any) => 
        allowedRoleIds.includes(a.delegation_role_id)
      );

      // Add current user if not in list
      if (currentAssignment && currentAssignment.user_id && currentAssignment.user) {
        const existsInList = rolePersonnel.some(p => p.userId === currentAssignment.user_id);
        if (!existsInList) {
          rolePersonnel.push({
            userId: currentAssignment.user_id,
            firstName: currentAssignment.user.first_name || '',
            middleName: currentAssignment.user.middle_name || '',
            lastName: currentAssignment.user.last_name || '',
            suffixName: currentAssignment.user.suffix_name?.name || '',
            fullName: getFullNameFn({
              firstName: currentAssignment.user.first_name,
              middleName: currentAssignment.user.middle_name,
              lastName: currentAssignment.user.last_name,
              suffixName: { name: currentAssignment.user.suffix_name?.name }
            }),
            delegationRoleId: currentAssignment.delegation_role_id,
            oid: currentAssignment.user.oid || ''
          });
        }
      }

      // Find error reason for this step if function provided
      let errorReason = '';
      if (findErrorReasonFn) {
        errorReason = findErrorReasonFn(step.oid, expiredPersonnel);
      }

      return {
        stepLabel: step.label,
        roleId: primaryRole?.delegationRoleId || null,
        allowedRoleIds: allowedRoleIds,
        personnel: rolePersonnel,
        selectedUserId: currentAssignment?.user_id || null,
        signatureStepId: step.oid,
        errorReason: errorReason
      };
    });
  }

  /**
   * Extract all role IDs from workflow steps
   */
  extractRoleIdsFromWorkflow(workflow: SignatureWorkflowResponseDto): number[] {
    if (!workflow?.steps) {
      return [];
    }

    const allRoleIds = new Set<number>();
    workflow.steps.forEach(step => {
      if (step.allowedRoles) {
        step.allowedRoles.forEach(role => {
          if (role.delegationRoleId) {
            allRoleIds.add(role.delegationRoleId);
          }
        });
      }
    });

    return Array.from(allRoleIds);
  }

  /**
   * Get error message from HTTP error
   */
  private getErrorMessage(error: any): string {
    let errorMessage = 'An unexpected error occurred';

    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    return errorMessage;
  }
}
